import { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { LoadingButton } from '@mui/lab';

import { client } from '@frontend/features/common/services';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { QueryKeys } from '@frontend/features/common/types';

// Keys como strings literales (no el enum de @bandido/shared) a propósito: así
// estos campos renderizan siempre, sin depender de que Vite re-optimice el dist
// de shared. El backend igual los lee vía el enum SettingsFields.
// La URL del proveedor NO está acá: vive en el env de deploy (anti-SSRF). La key
// nunca vuelve al frontend: se expone el boolean `*_CONFIGURED`.
const KEYS = {
  ENABLED: 'TRAVEL_INTELLIGENCE_ENABLED',
  API_KEY: 'TRAVEL_INTELLIGENCE_API_KEY',
  API_KEY_CONFIGURED: 'TRAVEL_INTELLIGENCE_API_KEY_CONFIGURED',
  BUSINESS_UNIT: 'TRAVEL_INTELLIGENCE_BUSINESS_UNIT',
  ORIGIN_COUNTRY: 'TRAVEL_INTELLIGENCE_ORIGIN_COUNTRY',
  PASSPORT_COUNTRY: 'TRAVEL_INTELLIGENCE_PASSPORT_COUNTRY',
  CURRENCY: 'TRAVEL_INTELLIGENCE_CURRENCY',
} as const;

interface FormValues {
  enabled: boolean;
  apiKey: string; // write-only: siempre arranca vacío, nunca se prellena
  businessUnit: string;
  originCountry: string;
  passportCountry: string;
  currency: string;
}

function toDefaults(settings?: Record<string, string>): FormValues {
  return {
    enabled: settings?.[KEYS.ENABLED] === 'true',
    apiKey: '',
    businessUnit: settings?.[KEYS.BUSINESS_UNIT] ?? '',
    originCountry: settings?.[KEYS.ORIGIN_COUNTRY] ?? '',
    passportCountry: settings?.[KEYS.PASSPORT_COUNTRY] ?? '',
    currency: settings?.[KEYS.CURRENCY] ?? '',
  };
}

interface Props {
  agencyId: number;
  settings?: Record<string, string>;
  isLoading?: boolean;
}

export function TravelIntelligenceSettings({
  agencyId,
  settings,
  isLoading,
}: Props) {
  const queryClient = useQueryClient();
  const { addToast } = useSnackBar();
  const { t } = useTranslation();
  const { control, handleSubmit, reset, watch } = useForm<FormValues>({
    defaultValues: toDefaults(settings),
  });
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);

  const hasApiKey = settings?.[KEYS.API_KEY_CONFIGURED] === 'true';
  const enabled = watch('enabled');

  // Sincroniza el form cuando llegan/cambian los settings cargados async: sin
  // esto, un form inicial vacío pisaría los valores guardados con strings vacíos.
  useEffect(() => {
    if (settings) reset(toDefaults(settings));
  }, [settings, reset]);

  const onSubmit = async (values: FormValues) => {
    setSaving(true);
    try {
      const payload: Record<string, string> = {
        [KEYS.ENABLED]: values.enabled ? 'true' : 'false',
        [KEYS.BUSINESS_UNIT]: values.businessUnit.trim(),
        [KEYS.ORIGIN_COUNTRY]: values.originCountry.trim(),
        [KEYS.PASSPORT_COUNTRY]: values.passportCountry.trim(),
        [KEYS.CURRENCY]: values.currency.trim(),
      };
      // La key solo se manda si el usuario escribió una nueva: si va vacía, el
      // backend preserva la guardada (write-only).
      if (values.apiKey.trim()) payload[KEYS.API_KEY] = values.apiKey.trim();

      await client.post(`settings/agency/${agencyId}`, payload);
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.getSettingsByAgencyId, agencyId.toString()],
      });
      // Limpiamos la key escrita para no dejarla en memoria del form.
      reset({ ...values, apiKey: '' });
      addToast({
        message: t('copilot.travelIntelligence.settings.saveSuccess'),
        color: 'success',
      });
    } catch {
      addToast({
        message: t('copilot.travelIntelligence.settings.saveError'),
        color: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  // No renderizamos el form editable hasta conocer el estado inicial de settings
  // (evita que un save temprano pise los valores guardados).
  if (isLoading || settings === undefined) {
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      maxWidth={700}
      display="flex"
      flexDirection="column"
      gap={3}
    >
      <Box>
        <Typography variant="h6" gutterBottom>
          {t('copilot.travelIntelligence.settings.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('copilot.travelIntelligence.settings.intro')}
        </Typography>
      </Box>

      <Controller
        name="enabled"
        control={control}
        render={({ field }) => (
          <FormControlLabel
            control={
              <Switch
                checked={field.value}
                onChange={(e) => field.onChange(e.target.checked)}
              />
            }
            label={t('copilot.travelIntelligence.settings.enable')}
          />
        )}
      />

      {enabled && (
        <Stack spacing={2}>
          {hasApiKey && (
            <Alert severity="info">
              {t('copilot.travelIntelligence.settings.apiKeyConfigured')}
            </Alert>
          )}
          <Controller
            name="apiKey"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                size="small"
                label={t('copilot.travelIntelligence.settings.apiKey')}
                type={showKey ? 'text' : 'password'}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowKey((v) => !v)}
                        edge="end"
                        size="small"
                      >
                        {showKey ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
          <Stack direction="row" spacing={2}>
            <Controller
              name="businessUnit"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  size="small"
                  label={t('copilot.travelIntelligence.settings.businessUnit')}
                  fullWidth
                />
              )}
            />
            <Controller
              name="currency"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  size="small"
                  label={t('copilot.travelIntelligence.settings.currency')}
                  fullWidth
                />
              )}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <Controller
              name="originCountry"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  size="small"
                  label={t('copilot.travelIntelligence.settings.originCountry')}
                  fullWidth
                />
              )}
            />
            <Controller
              name="passportCountry"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  size="small"
                  label={t(
                    'copilot.travelIntelligence.settings.passportCountry',
                  )}
                  fullWidth
                />
              )}
            />
          </Stack>
        </Stack>
      )}

      <Box display="flex" flexDirection="row-reverse">
        <LoadingButton
          type="submit"
          loading={saving}
          variant="contained"
          color="primary"
        >
          {t('copilot.travelIntelligence.settings.save')}
        </LoadingButton>
      </Box>
    </Box>
  );
}
